import { useCallback, useEffect, useState } from 'react'
import { getPatientLastMessages } from '../../api/getPatientLastMessages'
import { getUnreadMessages } from '../../api/getUnreadMessages'
import type { NormalizedUnreadMessage } from '../../types/unreadMessages'
import { mapUnreadMessagesResponse } from '../../utils/mapUnreadMessagesResponse'
import { UnreadMessageCard } from '../unread/UnreadMessageCard'
import type { MedicalLinkCredentialsLite } from '../unread/UnreadMessagesList'

import type { GetLastMessageUiRequest } from '../../types/unreadMessages'

export type GetLastMessageListProps = {
  request: GetLastMessageUiRequest
  getCredentials: () => MedicalLinkCredentialsLite
  parentMessageId: string
  onRowsLoaded?: (parentMessageId: string, rows: NormalizedUnreadMessage[]) => void
  onMessageOpen?: (parentMessageId: string, row: NormalizedUnreadMessage, index: number) => void
  hideReadAloudPrompt?: boolean
  onReadAloudRequest?: (parentMessageId: string, rows: NormalizedUnreadMessage[]) => void
  onReadAloudDecline?: (parentMessageId: string) => void
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; row: NormalizedUnreadMessage | null }

export function GetLastMessageList(props: GetLastMessageListProps) {
  const {
    request,
    getCredentials,
    parentMessageId,
    onRowsLoaded,
    onMessageOpen,
    hideReadAloudPrompt,
    onReadAloudRequest,
    onReadAloudDecline,
  } = props
  const [state, setState] = useState<LoadState>({ status: 'idle' })
  const fr = request.language === 'fr'

  const runLoad = useCallback(async () => {
    setState({ status: 'loading' })
    try {
      const c = getCredentials()
      const raw =
        request.scope === 'inbox_global'
          ? await getUnreadMessages({ bearerToken: c.piBearerToken, csrfToken: '' }, 0)
          : await getPatientLastMessages(
              { bearerToken: c.piBearerToken, csrfToken: '' },
              request.fbUid || '',
            )
      const mapped = mapUnreadMessagesResponse(raw, fr ? 'fr-FR' : 'en-US')
      const row = mapped[0] ?? null
      setState({ status: 'success', row })
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : fr
            ? 'Impossible de charger le message.'
            : 'Failed to load message.'
      setState({ status: 'error', message: msg })
    }
  }, [getCredentials, request.scope, request.fbUid, fr])

  useEffect(() => {
    void runLoad()
  }, [runLoad])

  useEffect(() => {
    if (state.status !== 'success') return
    onRowsLoaded?.(parentMessageId, state.row ? [state.row] : [])
  }, [state, parentMessageId, onRowsLoaded])

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="vnw-unreadList vnw-unreadList--loading" role="status" aria-live="polite">
        <div className="vnw-unreadListSpinner" aria-hidden="true" />
        <p className="vnw-unreadListLoadingText">
          {fr ? 'Chargement du message…' : 'Loading message…'}
        </p>
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <div className="vnw-unreadList vnw-unreadList--error" role="alert">
        <p className="vnw-unreadListErrorText">{state.message}</p>
        <button type="button" className="vnw-unreadListRetry" onClick={() => void runLoad()}>
          {fr ? 'Réessayer' : 'Retry'}
        </button>
      </div>
    )
  }

  if (!state.row) {
    return (
      <div className="vnw-unreadList vnw-unreadList--empty" role="status">
        <p className="vnw-unreadListEmptyTitle">
          {fr ? 'Aucun message trouvé' : 'No message found'}
        </p>
        <p className="vnw-unreadListEmptySub">
          {request.patientName
            ? fr
              ? `Aucun échange récent avec ${request.patientName}.`
              : `No recent exchange with ${request.patientName}.`
            : fr
              ? 'Aucun message récent.'
              : 'No recent message.'}
        </p>
      </div>
    )
  }

  const rows = [state.row]
  const showReadAloudOffer =
    Boolean(onReadAloudRequest && onReadAloudDecline) && !hideReadAloudPrompt

  return (
    <div className="vnw-unreadListWrap">
      <div className="vnw-unreadList" role="list" aria-label={fr ? 'Dernier message' : 'Last message'}>
        <div className="vnw-unreadListItem" role="listitem">
          <UnreadMessageCard
            message={state.row}
            onOpen={() => onMessageOpen?.(parentMessageId, state.row!, 0)}
          />
        </div>
      </div>
      {showReadAloudOffer ? (
        <div className="vnw-unreadReadAloudPrompt" role="group" aria-label={fr ? 'Lecture à voix haute' : 'Read aloud'}>
          <p className="vnw-unreadReadAloudPromptText">
            {fr
              ? 'Souhaitez-vous que je lise ce message à voix haute ?'
              : 'Would you like me to read this message aloud?'}
          </p>
          <div className="vnw-unreadReadAloudPromptBtns">
            <button
              type="button"
              className="vnw-unreadReadAloudBtn vnw-unreadReadAloudBtn--yes"
              onClick={() => onReadAloudRequest?.(parentMessageId, rows)}
            >
              {fr ? 'Lire à voix haute' : 'Read aloud'}
            </button>
            <button
              type="button"
              className="vnw-unreadReadAloudBtn vnw-unreadReadAloudBtn--no"
              onClick={() => onReadAloudDecline?.(parentMessageId)}
            >
              {fr ? 'Pas maintenant' : 'Not now'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
